import React, { useState, useEffect } from "react";
import { uploadLogoToCloudinarySigned } from "../../services/cloudinarySignedService";
import { useDispatch, useSelector } from "react-redux";
import { AlertTriangle, Save, Settings as SettingsIcon } from "lucide-react";
import toast from "../../utils/toast";
import { getClinic, updateClinic } from "../../store/slices/clinicSlice";

const ClinicSettings = () => {
  const dispatch = useDispatch();
  const { clinic, loading, error } = useSelector((state) => state.clinic);
  const [clinicInfo, setClinicInfo] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    working_hours: "",
    about_text: "",
    logo_url: "",
    verified_by: "",
  });
  const [testList, setTestList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingTests, setIsEditingTests] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState("");

  // Load clinic data when component mounts
  useEffect(() => {
    dispatch(getClinic());
  }, [dispatch]);

  // Update form when clinic data is loaded
  useEffect(() => {
    if (clinic) {
      setClinicInfo({
        name: clinic.name || "",
        address: clinic.address || "",
        phone: clinic.phone || "",
        email: clinic.email || "",
        working_hours: clinic.working_hours || "",
        about_text: clinic.about_text || "",
        logo_url: clinic.logo_url || "",
        verified_by: clinic.verified_by || "",
      });

      // Set test list if available
      if (clinic.test_list && Array.isArray(clinic.test_list)) {
        setTestList(clinic.test_list);
      } else {
        setTestList([]);
      }

      setIsInitialLoad(false);
    }
  }, [clinic]);

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  }, [error]);

  const handleSave = async () => {
    try {
      // Always include all fields, including verified_by
      const updateData = { ...clinicInfo, verified_by: clinicInfo.verified_by };
      if (isEditingTests) {
        updateData.test_list = testList;
      }
      await dispatch(updateClinic(updateData)).unwrap();
      setIsEditing(false);
      setIsEditingTests(false);
      toast.success("Clinic settings updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      dispatch(getClinic());
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update clinic settings", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClinicInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle logo file upload
  const handleLogoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoUploadError("");
    try {
      const url = await uploadLogoToCloudinarySigned(file);
      setClinicInfo((prev) => ({ ...prev, logo_url: url }));
      toast.success("Logo uploaded successfully!", { position: "top-right", autoClose: 2000 });
    } catch (err) {
      setLogoUploadError("Failed to upload logo. Please try again.");
      toast.error("Failed to upload logo", { position: "top-right", autoClose: 4000 });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleTestChange = (index, updates) => {
    setTestList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  // Keep your slug generator
  const generateSlug = (name) => {
    if (!name) return "";
    return name
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, "-");
  };

  // When adding a new test, keep total as a string so the input is editable
  const addNewTest = () => {
    setTestList((prev) => [
      ...prev,
      { name: "", slug: "", total: "", description: "" },
    ]);
  };

  const removeTest = (index) => {
    if (testList.length > 1) {
      setTestList((prev) => prev.filter((_, i) => i !== index));
    } else {
      toast.warning("At least one test is required", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleCancel = () => {
    // Reset form to original clinic data
    if (clinic) {
      setClinicInfo({
        name: clinic.name || "",
        address: clinic.address || "",
        phone: clinic.phone || "",
        email: clinic.email || "",
        working_hours: clinic.working_hours || "",
        about_text: clinic.about_text || "",
        logo_url: clinic.logo_url || "",
        verified_by: clinic.verified_by || "",
      });

      if (clinic.test_list && Array.isArray(clinic.test_list)) {
        setTestList(clinic.test_list);
      } else {
        setTestList([]);
      }
    }
    setIsEditing(false);
    setIsEditingTests(false);
  };

  // Debug: Log state changes
  useEffect(() => {}, [clinicInfo, testList, isEditing, isEditingTests]);

  // Show loading state during initial load
  if (isInitialLoad && loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative w-20 h-20 mx-auto">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-[#235F72] rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading clinic settings...</p>
        </div>
      </div>
    );
  }

  // Show error state if failed to load
  if (error && !clinicInfo.name) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Failed to Load Settings</h3>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => dispatch(getClinic())}
            className="px-6 py-3 bg-[#235F72] text-white rounded-xl hover:bg-[#1a4a5a] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#235F72] to-[#1a4a5a] bg-clip-text text-transparent">
              Clinic Settings
            </h1>
            <p className="text-gray-600 mt-1">Manage clinic info and available tests</p>
          </div>
          <div className="flex items-center space-x-3">
            {(isEditing || isEditingTests) && (
              <button
                onClick={handleCancel}
                disabled={loading}
                className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => {
                if (isEditing || isEditingTests) {
                  handleSave();
                } else {
                  setIsEditing(true);
                }
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-5 py-2.5 bg-[#235F72] text-white rounded-xl hover:bg-[#1a4a5a] transition-colors disabled:bg-gray-400"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? "Saving..." : isEditing || isEditingTests ? "Save All Changes" : "Edit Information"}</span>
            </button>
          </div>
        </div>
        {/* Clinic Information Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center space-x-2">
              <SettingsIcon className="w-5 h-5 text-[#235F72]" />
              <h2 className="text-lg font-bold text-gray-900">Clinic Information</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">Basic information and contact details</p>
          </div>

        {/* Clinic Information Form */}
        <div className="p-6 space-y-6">
          {/* Clinic Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinic Name *
            </label>
            <input
              type="text"
              name="name"
              value={clinicInfo.name}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Enter clinic name"
              required
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <input
              type="text"
              name="address"
              value={clinicInfo.address}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Enter clinic address"
              required
            />
          </div>

          {/* Contact Information - Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={clinicInfo.phone}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="Enter phone number"
                required
              />
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={clinicInfo.email}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="Enter email address"
                required
              />
            </div>
          </div>

          {/* Working Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Working Hours *
            </label>
            <input
              type="text"
              name="working_hours"
              value={clinicInfo.working_hours}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="e.g., Mon-Fri: 7:00 AM - 7:00 PM, Sat: 8:00 AM - 4:00 PM, Sun: Closed"
              required
            />
          </div>

          {/* About Clinic */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              About Clinic
            </label>
            <textarea
              name="about_text"
              value={clinicInfo.about_text}
              onChange={handleChange}
              disabled={!isEditing}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Describe your clinic's services, mission, and expertise..."
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinic Logo
            </label>
            <div className="flex items-start space-x-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                disabled={!isEditing || logoUploading}
                className="flex-1 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              />
              {logoUploading && (
                <div className="w-16 h-16 flex items-center justify-center">
                  <span className="text-xs text-gray-500">Uploading...</span>
                </div>
              )}
              {clinicInfo.logo_url && !logoUploading && (
                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden border border-gray-300">
                  <img
                    src={clinicInfo.logo_url}
                    alt="Clinic Logo Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
            {logoUploadError && (
              <p className="text-xs text-red-500 mt-1">{logoUploadError}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Upload a logo image for your clinic (PNG, JPG, etc.)
            </p>
          </div>

          {/* Verified By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verified By (for result printout)
            </label>
            <input
              type="text"
              name="verified_by"
              value={clinicInfo.verified_by}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="e.g. Dr. John Doe, Chief Pathologist"
            />
            <p className="text-xs text-gray-500 mt-1">
              This name will appear as the verifier on printed laboratory results.
            </p>
          </div>
        </div>
      </div>

        {/* Test List Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <SettingsIcon className="w-5 h-5 text-[#235F72]" />
                <h2 className="text-lg font-bold text-gray-900">Available Tests</h2>
              </div>
              <button
                onClick={() => setIsEditingTests(!isEditingTests)}
                disabled={loading}
                className="px-5 py-2.5 bg-[#235F72] text-white rounded-xl hover:bg-[#1a4a5a] transition-colors disabled:bg-gray-400"
              >
                {isEditingTests ? "View Mode" : "Edit Tests"}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">Manage tests and services offered</p>
          </div>

        <div className="p-6">
          {isEditingTests ? (
            <div className="space-y-4">
              {testList.map((test, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Test Name *
                      </label>
                      <input
                        type="text"
                        value={test.name || ""}
                        onChange={(e) => {
                          const name = e.target.value;
                          // Only auto-generate slug if it's empty (don’t overwrite user edits)
                          const nextSlug =
                            test.slug && test.slug.length > 0
                              ? test.slug
                              : generateSlug(name);
                          handleTestChange(index, { name, slug: nextSlug });
                        }}
                        onBlur={() => {
                          if (!test.slug && test.name) {
                            handleTestChange(index, {
                              slug: generateSlug(test.name),
                            });
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent bg-white"
                        placeholder="Enter test name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Slug *
                      </label>
                      <input
                        type="text"
                        value={test.slug || ""}
                        onChange={(e) =>
                          handleTestChange(index, { slug: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent bg-white"
                        placeholder="test-slug"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Tests Available
                      </label>
                      <input
                        type="number"
                        value={test.total === 0 ? 0 : test.total || ""} // allow empty while typing
                        onChange={(e) => {
                          // keep as string while typing; normalize on save
                          handleTestChange(index, { total: e.target.value });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent bg-white"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => removeTest(index)}
                        className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
                        type="button"
                      >
                        Remove Test
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={test.description || ""}
                      onChange={(e) =>
                        handleTestChange(index, { description: e.target.value })
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent bg-white"
                      placeholder="Describe this test..."
                    />
                  </div>
                </div>
              ))}
              <button
                onClick={addNewTest}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#235F72] hover:text-[#235F72] transition duration-200 bg-white"
                type="button"
              >
                + Add New Test
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testList.map((test, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition duration-200 bg-white"
                >
                  <h3 className="font-semibold text-[#235F72] mb-2">
                    {test.name || "Unnamed Test"}
                  </h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <span className="font-medium">Slug:</span>{" "}
                      {test.slug || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Available:</span>{" "}
                      {test.total || 0}
                    </p>
                    {test.description && (
                      <p>
                        <span className="font-medium">Description:</span>{" "}
                        {test.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {testList.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-500">
                  No tests available. Click "Edit Tests" to add tests.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

        {/* System Information Section */}
        {clinic && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Clinic ID:</span>
                <span className="ml-2 text-gray-600">{clinic.id}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Last Updated:</span>
                <span className="ml-2 text-gray-600">
                  {clinic.updated_at
                    ? new Date(clinic.updated_at).toLocaleString()
                    : "N/A"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created:</span>
                <span className="ml-2 text-gray-600">
                  {clinic.created_at
                    ? new Date(clinic.created_at).toLocaleString()
                    : "N/A"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total Tests for Landing page:</span>
                <span className="ml-2 text-gray-600">{testList.length}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicSettings;
